import { Badge, buttonClass, cardClass, Table } from '@ledgerloom/ui';
import { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useApi, usePageTitle, useStored } from '../hooks';
import { hasFeature, hasSurface, isAdmin, useSession } from '../session';
import { Empty, ErrorBanner, Icon, ListSkeleton, Time, ToggleGroup, ToneBadge, badgeTone } from '../components/ui';
import { Face, type Tone } from '../components/TeamFace';
import { micros } from '../format';
import { AgentWorkNav } from './AgentWork';

// The register: every agent row, what it acts for, its status, queue, and cost,
// as cards or as a table; this browser keeps the choice. Hiring is its own page
// (AgentHire). Patterns: data-display/card, data-display/table.

export interface AgentRow {
  id: string; template_id: string; name: string; title: string; class: string; status: string; status_reason: string | null; description: string;
  principal_type: string; principal_id: string | null; principal_name: string | null; principal_status: string | null; owner_user_id: string | null; owner_name: string | null;
  playbook_id: string; playbook_version: number; playbook_status: string;
  queued_tasks: number; last_run: { status: string; started_at: string } | null; cost_month_micros: string; envelope: Record<string, any>;
  triggers: Array<{ class?: string }>;
}
export const builtHere = (templateId: string) => templateId.startsWith('local/');

export const STATUS_TONE: Record<string, string> = { active: 'ok', shadow: 'info', draft: '', suspended: 'danger', retired: '' };
export const STATUS_LABEL: Record<string, string> = { active: 'Active', shadow: 'Shadow', draft: 'Draft', suspended: 'Suspended', retired: 'Retired' };
const STATUS_RING: Record<string, Tone> = { active: 'work', shadow: 'trial', draft: 'attn', suspended: 'stuck', retired: 'idle' };
// The kind of work, in a few words.
export const KIND_WORDS: Record<string, string> = { automate: 'Works on its own', assist: 'Drafts for a person', advise: 'Reads and explains', configure: 'Sets things up' };
export const CLASS_HELP: Record<string, string> = {
  assist: 'Drafts for one person, who approves every proposal',
  automate: 'Works end to end inside its envelope; exceptions go to a person',
  advise: 'Reads and explains; never proposes',
  configure: 'Proposes change sets that a person signs',
};

// Whom the agent works for, in plain words, and a warning when that person or team is gone:
// work for them goes to a team or company agent instead, so the row wants a new person or retiring.
export function WorksFor({ a }: { a: Pick<AgentRow, 'principal_type' | 'principal_name' | 'principal_status'> }) {
  if (a.principal_type === 'tenant') return <>The company</>;
  const gone = a.principal_status !== null && a.principal_status !== 'active';
  return <>{a.principal_name ?? (a.principal_type === 'team' ? 'A team' : 'A person')}{gone && <> <Badge tone="warning">{a.principal_type === 'team' ? 'Team archived' : 'Has left'}</Badge></>}</>;
}

export function Agents() {
  const { me } = useSession();
  const [showRetired, setShowRetired] = useState(false);
  const agents = useApi<AgentRow[]>(`/v1/agents${showRetired ? '?include_retired=true' : ''}`, [showRetired]);
  // A person with agents of their own sees those first; a manager, who runs them all, sees everyone. The other view is one press away.
  const [who, setWho] = useState<'me' | 'all'>(isAdmin(me) || me.role === 'manager' ? 'all' : 'me');
  const [view, setView] = useStored<'cards' | 'list'>('ll.agents.view', 'cards');
  const myAgents = agents.data?.filter((a) => a.principal_type === 'user' && a.principal_id === me.user_id) ?? [];
  const rows = who === 'me' && myAgents.length ? myAgents : agents.data ?? [];
  const [params] = useSearchParams();
  usePageTitle('Agents');
  // A hire signed by someone else appears without a reload.
  const reload = agents.reload;
  useEffect(() => { const t = setInterval(() => void reload(), 10_000); return () => clearInterval(t); }, [reload]);
  const admin = isAdmin(me) || me.role === 'manager';
  // The builder is offered while it is hired; on trial its page says it is not turned on yet.
  const builder = hasFeature(me, 'agent_builder') && hasSurface(me, 'agent_builder');
  // Links from before hiring had its own page.
  if (params.get('hire') === '1') return <Navigate to={`/agents/hire${params.get('template') ? `?template=${encodeURIComponent(params.get('template')!)}` : ''}`} replace />;
  return (
    <div className="stack">
      <AgentWorkNav />
      <div className="page-head">
        <div><h1>Agents</h1><p>Every agent hired here, who it works for, and how it is doing. New agents start on trial: their work is checked and logged, never saved or sent.</p></div>
        {admin && (
          <div className="row">
            {builder && <Link className={buttonClass({ variant: 'secondary' })} to="/agents/build"><Icon name="agent" />Build an agent</Link>}
            <Link className={buttonClass()} to="/agents/hire"><Icon name="plus" />Hire an agent</Link>
          </div>
        )}
      </div>
      <ErrorBanner error={agents.error} />
      <div className="contacts-toolbar">
        {myAgents.length > 0 && <ToggleGroup label="Whose agents" value={who} options={[['me', 'Working for you'], ['all', 'Everyone']]} onChange={setWho} />}
        <label className="row small"><input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} />Show retired</label>
        <span className="grow" />
        <ToggleGroup label="Show agents as" value={view} options={[['cards', 'Cards'], ['list', 'List']]} onChange={setView} />
      </div>
      {agents.loading && !agents.data ? <div className={cardClass}><ListSkeleton rows={5} label="Loading hired agents" /></div> : !agents.data?.length ? (
        <div className={cardClass}><Empty title="No agents yet" action={admin ? <Link className={buttonClass()} to="/agents/hire"><Icon name="plus" />Hire an agent</Link> : undefined}>Agents you hire show here, each with who it works for and how it is doing.</Empty></div>
      ) : view === 'cards' ? (
        <ul className="member-grid" aria-label="Hired agents">
          {rows.map((a) => (
            <li key={a.id}>
              <div className={`${cardClass} member agent-card`}>
                <span className="member-top">
                  <Face kind="agent" name={a.name} template={a.template_id} tone={STATUS_RING[a.status]} />
                  <span className="grow">
                    <Link className="member-name card-link" to={`/agents/${encodeURIComponent(a.id)}`}>{a.name}</Link>
                    <span className="faint">{a.title !== a.name ? a.title : KIND_WORDS[a.class] ?? 'Agent'}</span>
                  </span>
                  <ToneBadge tone={STATUS_RING[a.status] ?? 'idle'}>{STATUS_LABEL[a.status] ?? a.status}</ToneBadge>
                </span>
                <span className="member-sentence">{a.principal_type === 'tenant' ? 'Works for the company' : <>Works for <WorksFor a={a} /></>}{builtHere(a.template_id) && <> <Badge tone="info">Built here</Badge></>}</span>
                <span className="member-meta faint small">
                  {a.queued_tasks} queued · {a.last_run ? <>last ran <Time iso={a.last_run.started_at} /></> : 'has not run yet'} · {micros(a.cost_month_micros)} this month
                </span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <section className={cardClass} aria-label="Hired agents">
          <Table label="Hired agents">
              <caption className="sr-only">Agents in this workspace</caption>
              <thead><tr><th scope="col">Agent</th><th scope="col">Status</th><th scope="col">Works for</th><th scope="col" className="hide-sm">Owner</th><th scope="col" className="hide-sm">Playbook</th><th scope="col">Queue</th><th scope="col" className="hide-sm">Last run</th><th scope="col" className="hide-sm">Cost this month</th></tr></thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id}>
                    <td><div className="row" style={{ gap: 6 }}><Link to={`/agents/${encodeURIComponent(a.id)}`}>{a.name}</Link>{builtHere(a.template_id) && <Badge tone="info">Built here</Badge>}</div><div className="faint">{a.title} · <span style={{ textTransform: 'capitalize' }}>{a.class}</span></div></td>
                    <td><Badge tone={badgeTone(STATUS_TONE[a.status] ?? '')}>{STATUS_LABEL[a.status] ?? a.status}</Badge></td>
                    <td className="small"><WorksFor a={a} /></td>
                    <td className="small hide-sm">{a.owner_name ?? <span className="faint">Nobody</span>}</td>
                    <td className="small hide-sm">v{a.playbook_version} {a.playbook_status === 'signed' ? <span className="faint">signed</span> : <Badge tone="warning">unsigned</Badge>}</td>
                    <td>{a.queued_tasks}</td>
                    <td className="small hide-sm">{a.last_run ? <><Time iso={a.last_run.started_at} /> · {a.last_run.status}</> : <span className="faint">Never</span>}</td>
                    <td className="small hide-sm">{micros(a.cost_month_micros)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
        </section>
      )}
    </div>
  );
}
